import React, { useEffect, useMemo, useRef, useState } from "react";
import LiveConcertSvg from "./LiveConcert.svg";

import styles from "./LiveConcertDiagram.module.scss";

export type LiveConcertItem = "marlowe" | "skal" | "laura" | "buff" | "recordings";

export type DomHandles = LiveConcertItem | "microphones";

interface LiveConcertDiagramProps {
  onItemSelect: (item: LiveConcertItem) => void;
  activeItems: LiveConcertItem[];
}

export const LiveConcertDiagram: React.FC<LiveConcertDiagramProps> = ({ onItemSelect, activeItems }) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState<boolean>(false);

  useEffect(() => {
    setMounted(true);
  }, [divRef]);

  // memoize item handles
  const itemHandles = useMemo<Partial<Record<DomHandles, Element>>>(() => {
    if (!mounted) {
      return {};
    }

    return {
      marlowe: divRef.current!.querySelector(".marlowe") ?? undefined,
      laura: divRef.current!.querySelector(".laura") ?? undefined,
      buff: divRef.current!.querySelector(".buff") ?? undefined,
      skal: divRef.current!.querySelector(".skal") ?? undefined,
      recordings: divRef.current!.querySelector(".recordings") ?? undefined,
      microphones: divRef.current!.querySelector(".microphones") ?? undefined,
    };
  }, [divRef, mounted]);

  // setup click listeners
  useEffect(() => {
    const listeners: Record<string, () => void> = {};

    Object.entries(itemHandles).forEach(([id, handle]) => {
      handle.classList.add("item");
      listeners[id] = () => onItemSelect(id as LiveConcertItem);
      handle.addEventListener("click", listeners[id]);
    });

    itemHandles["marlowe"]?.classList.add("active");

    return () => {
      Object.entries(itemHandles).forEach(([id, handle]) => {
        handle.removeEventListener("click", listeners[id]);
      });
    };
  }, [mounted, itemHandles, onItemSelect]);

  // add active class based on props
  useEffect(() => {
    Object.entries(itemHandles).forEach(([id, handle]) => {
      if (activeItems?.includes(id as LiveConcertItem)) {
        handle.classList.add("active");
      } else {
        handle.classList.remove("active");
      }
    });

    if (activeItems.includes("recordings")) {
      itemHandles.microphones?.classList.add("visible");
    } else {
      itemHandles.microphones?.classList.remove("visible");
    }
  }, [itemHandles, activeItems]);

  return (
    <div ref={divRef} className={styles.root}>
      <LiveConcertSvg />
    </div>
  );
};
